import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Sparkles } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast.success('Message sent successfully!');
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        Object.values(errors).forEach((msgs: any) => toast.error(msgs[0]));
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-12">
      {/* Immersive Support Hero Banner */}
      <div className="bg-primary text-white py-20 px-4 text-center rounded-b-[40px] relative overflow-hidden shadow-premium">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E1510] via-primary-500 to-[#150E0A]" />
        <div className="relative z-10 space-y-4 max-w-xl mx-auto">
          <span className="inline-flex items-center gap-1.5 bg-accent/20 text-accent text-[9px] font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full">
            <Sparkles size={11} /> Royal Pet Store Helpdesk
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-extrabold tracking-tight">Contact Customer Care</h1>
          <p className="text-gray-300 text-xs md:text-sm font-medium leading-relaxed max-w-md mx-auto">
            Get in touch for pet adoption checklists, organic food diets, orders tracking, or veterinary guidance.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Side: Contact details */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-premium space-y-8">
              <div>
                <h2 className="text-sm font-extrabold text-primary mb-6 uppercase tracking-widest border-b border-gray-50 pb-2">Our Channels</h2>
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FCFAF7] border border-accent/15 rounded-xl flex items-center justify-center flex-shrink-0 text-accent shadow-sm">
                      <Phone size={18} />
                    </div>
                    <div className="space-y-0.5 text-xs font-semibold text-gray-650">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Phone / WhatsApp Helpline</p>
                      <a href="tel:03295025172" className="text-sm text-primary hover:text-accent font-extrabold transition-colors">0329 5025172</a>
                      <p className="text-[10px] text-gray-400 font-medium">Daily response: 9AM – 6PM</p>
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FCFAF7] border border-accent/15 rounded-xl flex items-center justify-center flex-shrink-0 text-accent shadow-sm">
                      <Mail size={18} />
                    </div>
                    <div className="space-y-0.5 text-xs font-semibold text-gray-650">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Official Email Inquiries</p>
                      <a href="mailto:info@royalpetstore.store" className="text-sm text-primary hover:text-accent font-extrabold transition-colors">info@royalpetstore.store</a>
                      <p className="text-[10px] text-gray-400 font-medium">We reply within 24 hours</p>
                    </div>
                  </div>

                  {/* Location Address */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FCFAF7] border border-accent/15 rounded-xl flex items-center justify-center flex-shrink-0 text-accent shadow-sm">
                      <MapPin size={18} />
                    </div>
                    <div className="space-y-0.5 text-xs font-semibold text-gray-655">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Main Pet Center</p>
                      <p className="text-sm text-primary font-extrabold">Royal Pet Store</p>
                      <p className="text-[11px] text-gray-500 font-medium">Bakra mandi dhok syedan road kamalabad Rawalpindi</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-[#FCFAF7] rounded-2xl p-5 border border-gray-100/60 text-xs font-semibold">
                <h3 className="text-[10px] font-extrabold text-primary uppercase tracking-widest mb-3 border-b border-gray-150 pb-1.5">Business Operations</h3>
                <div className="space-y-2.5">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monday – Saturday</span>
                    <span className="text-primary font-bold">9:00 AM – 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sunday Helpline</span>
                    <span className="text-red-500 font-bold">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Message form */}
          <div className="lg:col-span-7">
            {sent ? (
              <div className="flex flex-col items-center justify-center min-h-[450px] text-center p-10 bg-green-50 rounded-[32px] border border-green-150 shadow-sm space-y-4">
                <div className="w-14 h-14 bg-green-500/10 text-green-600 rounded-full flex items-center justify-center shadow-sm">
                  <CheckCircle size={32} />
                </div>
                <h2 className="text-2xl font-serif font-extrabold text-primary">Message Dispatched!</h2>
                <p className="text-gray-550 text-xs font-semibold max-w-sm leading-relaxed">
                  Thank you for contacting Royal Pet Store. We have received your inquiry and our support team will get back to you within 24 hours.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                  className="mt-4 bg-primary hover:bg-accent text-white px-8 py-3.5 text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-sm active:scale-95"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 rounded-[32px] border border-gray-100 shadow-premium space-y-6">
                <h2 className="text-sm font-extrabold text-primary uppercase tracking-widest border-b border-gray-50 pb-2 mb-2">Send a Message</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-extrabold text-primary uppercase tracking-wider">Full Name *</label>
                    <input
                      type="text" 
                      name="name" 
                      value={form.name} 
                      onChange={handleChange} 
                      required
                      placeholder="e.g. Asad Khan"
                      className="w-full border border-gray-200 px-4 py-3 text-xs font-semibold rounded-xl focus:outline-none focus:border-accent bg-gray-50/50 transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[10px] font-extrabold text-primary uppercase tracking-wider">Email Address *</label>
                    <input
                      type="email" 
                      name="email" 
                      value={form.email} 
                      onChange={handleChange} 
                      required
                      placeholder="e.g. name@domain.com"
                      className="w-full border border-gray-200 px-4 py-3 text-xs font-semibold rounded-xl focus:outline-none focus:border-accent bg-gray-50/50 transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-extrabold text-primary uppercase tracking-wider">Inquiry Subject *</label>
                  <select
                    name="subject" 
                    value={form.subject} 
                    onChange={handleChange} 
                    required
                    title="Select Subject"
                    className="w-full border border-gray-200 px-4 py-3 text-xs font-bold rounded-xl focus:outline-none focus:border-accent bg-[#FCFAF7] transition-all"
                  >
                    <option value="" className="font-semibold text-gray-400">Select inquiry topic...</option>
                    <option value="Adoption Inquiry" className="font-semibold">Adoption Checklists & Breeds</option>
                    <option value="Feed & Treats" className="font-semibold">Organic Feed Nutrition</option>
                    <option value="Pet Accessories" className="font-semibold">Accessories & Bedding</option>
                    <option value="Veterinary Health" className="font-semibold">Veterinary health clearance logs</option>
                    <option value="Transit & Shipping" className="font-semibold">Climate Transport Schedules</option>
                    <option value="Other" className="font-semibold">General Question</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-extrabold text-primary uppercase tracking-wider">Your Message *</label>
                  <textarea
                    name="message" 
                    value={form.message} 
                    onChange={handleChange} 
                    required 
                    rows={5}
                    placeholder="Describe your inquiry in details..."
                    className="w-full border border-gray-200 px-4 py-3 text-xs font-semibold rounded-xl focus:outline-none focus:border-accent bg-gray-50/50 transition-colors resize-none"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-primary hover:bg-accent text-white py-4 text-xs font-bold uppercase tracking-widest rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-premium hover:-translate-y-0.5 active:scale-98"
                  >
                    {loading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Transmitting...</>
                    ) : (
                      <><Send size={14} /> Dispatch Message</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
